import { isServer } from 'tradera/utils/nextjs';
import { webApi } from 'tradera/state/services/webapi-api';

import type {
    GetNotificationsApiResponse,
    SetLastNotifiedApiResponse
} from 'tradera/state/services/types/webapi-member-generated';
import qs from 'qs';
import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';

export const NOTIFICATIONS_ENDLESS_PAGESIZE = 50;

export const notificationsApi = webApi.injectEndpoints({
    endpoints: (builder) => ({
        getUnreadNotificationsCount: builder.query<number, void>({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/unread-notifications-count`;
            },
            transformResponse: (result: GetNotificationsApiResponse) =>
                result.unreadCount
        }),
        getNotificationsEndless: builder.query<
            GetNotificationsApiResponse & { page: number },
            { page?: number }
        >({
            query: ({ page = 1 }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                const params = qs.stringify(
                    {
                        take: NOTIFICATIONS_ENDLESS_PAGESIZE,
                        skip: (page - 1) * NOTIFICATIONS_ENDLESS_PAGESIZE
                    },
                    { addQueryPrefix: true }
                );
                return `${baseUrl}/member/notifications${params}`;
            },
            transformResponse: (
                response: GetNotificationsApiResponse,
                _,
                request
            ) => {
                return { ...response, page: request.page ?? 1 };
            },
            serializeQueryArgs: ({ endpointDefinition, endpointName }) => {
                // Exclude pageNumber from queryArgs to be ablue to use same cache for infiniteScroll
                return defaultSerializeQueryArgs({
                    endpointName,
                    queryArgs: {},
                    endpointDefinition
                });
            },
            forceRefetch: ({ currentArg, previousArg }) => {
                return currentArg?.page !== previousArg?.page;
            },
            merge: (currentCacheData, response) => {
                return {
                    ...currentCacheData,
                    items: [
                        ...(currentCacheData.items ?? []),
                        ...(response.items ?? [])
                    ],
                    unreadCount: response.unreadCount,
                    totalCount: response.totalCount,
                    page: response.page
                };
            },
            // onQueryStarted is used here for automatically mark the notifications as read after loading data first page.
            onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
                if (arg.page && arg.page > 1) return;
                const result = await queryFulfilled;
                if (result?.data?.unreadCount) {
                    dispatch(endpoints.clearAllUnreadNotifications.initiate());
                }
            }
        }),
        clearAllUnreadNotifications: builder.mutation<
            SetLastNotifiedApiResponse,
            void
        >({
            query: () => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/lastnotified`,
                    method: 'POST'
                };
            },
            onQueryStarted: async (_, { dispatch }) => {
                dispatch(
                    notificationsApi.util.upsertQueryData(
                        'getUnreadNotificationsCount',
                        undefined,
                        0
                    )
                );
            }
        })
    })
});

export const {
    useGetUnreadNotificationsCountQuery,
    useGetNotificationsEndlessQuery,
    endpoints
} = notificationsApi;
